/* File: src/constants/gameConstants.js */

/* src/constants/gameConstants.js */

// --- Conceptual Difficulty Tiers (Based on 0.0 - 1.0 scale) ---
// Used for calculations where a 0-1 range provides a smoother scaling factor.
// IMPORTANT: Core scaling logic is now backend, but these might be useful
// for frontend understanding or potential future visualisations.
export const DIFFICULTY_TIERS_CONCEPTUAL = {
  NOVICE: { name: "Novice", range: [0.0, 0.2], color: "novice" },
  APPRENTICE: { name: "Apprentice", range: [0.2, 0.4], color: "apprentice" },
  ADEPT: { name: "Adept", range: [0.4, 0.6], color: "adept" },
  EXPERT: { name: "Expert", range: [0.6, 0.8], color: "expert" },
  MASTER: { name: "Master", range: [0.8, 1.0], color: "master" },
};

// --- Precise Difficulty Tiers (Based on 0.00 - 0.95 scale) ---
// Used primarily for the FocusMeter display text, label highlighting, etc.
// This is safe to keep on the frontend for UI purposes.
export const DIFFICULTY_TIERS_DISPLAY = {
  NOVICE: {         // Levels: N.00, N.05, N.10, N.15
    name: "Novice",
    min: 0.00,
    max: 0.15,
    color: "novice" // Tailwind class association (defined in tailwind.config.js)
  },
  APPRENTICE: {     // Levels: N.20, N.25, N.30, N.35
    name: "Apprentice",
    min: 0.20,
    max: 0.35,
    color: "apprentice"
  },
  ADEPT: {          // Levels: N.40, N.45, N.50, N.55
    name: "Adept",
    min: 0.40,
    max: 0.55,
    color: "adept"
  },
  EXPERT: {         // Levels: N.60, N.65, N.70, N.75
    name: "Expert",
    min: 0.60,
    max: 0.75,
    color: "expert"
  },
  MASTER: {         // Levels: N.80, N.85, N.90, N.95
    name: "Master",
    min: 0.80,
    max: 0.99,
    color: "master"
  }
};

// Default export: Use the DISPLAY tiers for components like FocusMeter
export const DIFFICULTY_TIERS = DIFFICULTY_TIERS_DISPLAY;

// --- Feedback Messages ---
// Used by frontend (GameResults) to display feedback text received from backend/context.
// Safe to keep on frontend.
export const FEEDBACK_MESSAGES = {
  DIFFICULTY_INCREASE: {
    SMALL: [
      "Focus improving! Keep it up.",
      "Good progress! Challenge slightly increased.",
      "You're getting better! Difficulty adjusted up."
    ],
    LARGE: [
      "Excellent progress! Challenge increased.",
      "Outstanding performance! Difficulty boosted.",
      "Impressive focus! Challenge level raised."
    ]
  },
  DIFFICULTY_MAINTAINED: [
    "Maintaining {tierName} level.", // {tierName} will be filled by getTierName(preciseDifficulty)
    "Consistent performance. Keep practicing.",
    "Steady progress at {tierName} level."
  ],
  DIFFICULTY_DECREASE: {
    SMALL: [
      "Let's adjust the challenge slightly.",
      "Small adjustment to help you improve.",
      "Recalibrating for better learning."
    ],
    MEDIUM: [
      "Recalibrating difficulty for better learning.",
      "Adjusting challenge to optimize learning.",
      "Reducing difficulty to build stronger foundations."
    ],
    LARGE: [
      "Adjusting to help you build mastery.",
      "Recalibrating for improved learning.",
      "Setting a more suitable challenge level."
    ]
  }
};

// --- Feature Introduction Messages ---
// Used by frontend (GameResults) based on NEW difficulty level.
// Safe to keep on frontend.
export const FEATURE_INTRODUCTION = {
  0.2: "Audio speed is faster (1.25x). Trust your quick hearing.",
  0.3: "Audio letters sound more alike. Trust your ear to distinguish.",
  0.4: "Audio speed increases again (1.5x). Adapt to the rhythm.",
  0.45: "Visual & audio matches can occur together. Broaden your awareness.",
  0.5: "The overall pace is quicker.",
  0.6: "Audio speed increases (1.75x). Handle 'near misses' (lures) by trusting the true {n}-back signal.",
  0.7: "Audio letters are highly similar. Trust your sharpest perception.",
  0.8: "Audio speed is at maximum (2x). Stay present and centered.",
};

// --- Coaching Messages ---
// Used by frontend (GameCountdown) based on precise difficulty.
// Safe to keep on frontend.
export const COACHING_MESSAGES = {
  // --- NOVICE (N.00 - N.19) ---
  // Goal: Presence, rhythm, letting go.
  NOVICE: [
    "Deep breath... You got this!", 
    "Let’s warm up that brain.",
    "Just follow the rhythm.",
    "Eyes and ears ready.",
    "One step at a time.",
  ],
  // For difficulties 0.20 - 0.39 (precise)
  APPRENTICE: [
    "Stay present, stay focused.", 
    "Keep that streak alive!",
    "Let’s dial it in.",
    "Keep calm and N-back on.", 
    "Trust the process. Your brain learns!", 
  ],
  // For difficulties 0.40 - 0.59 (precise)
  ADEPT: [
    "Time to level up.",
    "Embrace the challenge!", 
    "Lock it in.",
    "Beat your best.",
    "Warning: May cause enhanced focus.", 
  ],
  // For difficulties 0.60 - 0.79 (precise)
  EXPERT: [
    "Deep focus zone engaged.", // Motivational/Intense
    "Time to shine!", // Motivational
    "Your N-back senses are tingling!", // Funny/Theme-related
    "Push your limits!", // Motivational
    "Warning: Brain operating at high capacity.", // Funny/Acknowledging difficulty
  ],
  // For difficulties 0.80 - 0.99 (precise)
  MASTER: [
    "Unleash your N-back mastery!",
    "Pure focus. Pure flow.", 
    "You make this look easy!", 
    "Be the N-back.", 
    "Welcome to the N-back matrix.", 
  ]
};

// --- D-prime (d′) thresholds for *interpreting* performance (used in results screen text) ---
// This is needed by the frontend GameResults component for display text. Safe to keep.
export const DPRIME_THRESHOLDS_INTERPRETATION = {
    EXCELLENT: 3.5,
    VERY_GOOD: 2.8, // Note: Backend uses dynamic 'goodThreshold' for progression, this is for display text only
    GOOD: 2.0,
    FAIR: 1.5,
    POOR: 1.0
};

// --- Default Profiles (Keep for potential local dev/initialization fallback) ---
// This is only used if ProfileContext fails or for initial setup, not core logic.
export const DEFAULT_PROFILES = [
  {
    id: "profile1",
    name: "Allen",
    currentNBack: 3,
    currentDifficulty: 0.75,
    highestNBack: 3,
    consecutiveSuccesses: 0,
    sessions: [
      { date: "2025-03-24T10:00:00Z", nBack: 3, difficulty: 0.70, accuracy: 85, overallDprime: 2.9, newNBack: 3, newDifficulty: 0.75, consecutiveSuccesses: 0 },
      { date: "2025-03-23T15:30:00Z", nBack: 3, difficulty: 0.75, accuracy: 92, overallDprime: 3.6, newNBack: 3, newDifficulty: 0.75, consecutiveSuccesses: 0 }
    ],
    journal: [], // Ensure default structure
    journalTags: []
  },
  {
    id: "profile2",
    name: "Monica",
    currentNBack: 2,
    currentDifficulty: 0.15,
    highestNBack: 2,
    consecutiveSuccesses: 0,
    sessions: [
       { date: "2025-03-24T11:00:00Z", nBack: 2, difficulty: 0.15, accuracy: 78, overallDprime: 1.8, newNBack: 2, newDifficulty: 0.15, consecutiveSuccesses: 0 },
       { date: "2025-03-23T16:00:00Z", nBack: 2, difficulty: 0.10, accuracy: 82, overallDprime: 2.1, newNBack: 2, newDifficulty: 0.15, consecutiveSuccesses: 0 }
    ],
    journal: [], // Ensure default structure
    journalTags: []
  },
];

// --- Stimuli (Default English Set) ---
// This is the fallback if language-specific letters aren't used or for English.
// The backend (sequence generator) will primarily use LANGUAGE_AUDIO_LETTERS if language is specified.
export const STIMULI = {
  AUDIO_LETTERS: [
    // New single letter set for English (and default fallback on frontend)
    // Used for all N.DD difficulty levels.
    ['C', 'H', 'K', 'L', 'Q', 'R', 'S', 'T']
  ],
  // Visual grid positions remain the same
  VISUAL_POSITIONS: [0, 1, 2, 3, 4, 5, 6, 7, 8]
};


// --- Language-Specific Audio Letter Sets (Native Characters) ---
// This section now contains the native character sets you provided.
export const LANGUAGE_AUDIO_LETTERS = {
  'en': { 
    name: 'English',
    letters: [
      // New single letter set for English, used for all N.DD difficulty levels.
      ['C', 'H', 'K', 'L', 'Q', 'R', 'S', 'T']
    ]
  },
  'cmn-TW': {
    name: 'Mandarin (Zhuyin)',
    letters: [ 
      ['ㄅ', 'ㄘ', 'ㄉ', 'ㄍ', 'ㄏ', 'ㄎ', 'ㄇ', 'ㄙ']
    ]
  },
  'es-ES': {
    name: 'Spanish (Spain)',
    letters: [ 
      ['K', 'Z', 'Ñ', 'J', 'V', 'X', 'Y', 'F']
    ]
  },
  'hi-IN': {
    name: 'Hindi (India)',
    letters: [ 
      ['क', 'झ', 'ङ', 'ठ', 'घ', 'फ', 'ञ', 'श']
    ]
  },
  'ar-XA': {
    name: 'Arabic (Standard)',
    letters: [ 
      ['خ', 'ق', 'ح', 'ذ', 'ش', 'ظ', 'غ', 'ض']
    ]
  },
  'bn-IN': {
    name: 'Bengali (India)',
    letters: [ 
      ['ঙ', 'ঠ', 'ঝ', 'ঘ', 'ছ', 'ফ', 'ষ', 'হ']
    ]
  },
  'pt-PT': {
    name: 'Portuguese (Portugal)',
    letters: [ 
      ['V', 'Z', 'J', 'Q', 'X', 'Ç', 'NH', 'LH']
    ]
  },
  'ja-JP': {
    name: 'Japanese (Japan)',
    letters: [ 
      ['ら', 'ぬ', 'も', 'け', 'や', 'ふ', 'ち', 'ぜ']
      ]
  },
  'de-DE': {
    name: 'German (Germany)',
    letters: [ 
      ['Z', 'W', 'J', 'Ü', 'Ä', 'Ö', 'Q', 'X']
      ]
  },
  'fr-FR': {
    name: 'French (France)',
    letters: [ 
      ['J', 'Z', 'U', 'Y', 'Q', 'X', 'É', 'H']
      ]
  },
  'it-IT': {
    name: 'Italian (Italy)',
    letters: [ 
      ['Z', 'Q', 'H', 'V', 'U', 'J', 'Y', 'X']
    ]
  },
  'pl-PL': {
    name: 'Polish (Poland)',
    letters: [ 
      ['Ł', 'Ć', 'Ń', 'Ś', 'Ó', 'Ę', 'H', 'Ź']
    ]
  }
};

// --- ALGORITHM_PARAMS is REMOVED from Frontend Constants ---
// The sensitive algorithm parameters defining progression and scaling
// are now only present in `functions/gameConstants.js`